import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import RenderMonitor from '../components/RenderMonitor';
import {
  deleteClientById,
  invalidateClientSnapshot,
  updateClientsListCache,
} from '../utils/clientManagement';

function AdminClientPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const passedClientName = location?.state?.clientName;

  const [client, setClient] = useState(null);
  const [regions, setRegions] = useState([]);
  const [teamCounts, setTeamCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editingName, setEditingName] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');

  const [showRegionModal, setShowRegionModal] = useState(false);
  const [newRegionName, setNewRegionName] = useState('');
  const [regionModalError, setRegionModalError] = useState('');
  const [addingRegion, setAddingRegion] = useState(false);

  const [showDeleteClientModal, setShowDeleteClientModal] = useState(false);
  const [deletingClient, setDeletingClient] = useState(false);
  const [deleteClientError, setDeleteClientError] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        let clientInfo = null;
        if (passedClientName) {
          clientInfo = { id: Number(id), business_name: passedClientName };
        } else {
          const { data: clientData, error: clientErr } = await supabase
            .from('clients')
            .select('id, business_name')
            .eq('id', id)
            .single();
          if (clientErr || !clientData) {
            if (mounted) setError('Client not found');
            return;
          }
          clientInfo = clientData;
        }

        const { data: regionsData, error: regionsErr } = await supabase
          .from('regions')
          .select('id, name')
          .eq('client_id', id)
          .eq('active', true)
          .order('name');

        if (regionsErr) {
          if (mounted) setError('Failed to load regions');
          return;
        }

        const { data: teamsData } = await supabase
          .from('teams')
          .select('region_id')
          .eq('client_id', id)
          .eq('active', true);

        const counts = {};
        (teamsData || []).forEach((t) => {
          const key = String(t.region_id);
          counts[key] = (counts[key] || 0) + 1;
        });

        if (!mounted) return;
        setClient(clientInfo);
        setRegions(regionsData || []);
        setTeamCounts(counts);
      } catch {
        if (mounted) setError('Failed to load client');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [id, passedClientName, location.key]);

  const handleSaveName = async () => {
    const name = (newClientName || '').trim();
    if (!name) {
      setNameError('Name is required');
      return;
    }
    try {
      setSavingName(true);
      const { data, error: updErr } = await supabase
        .from('clients')
        .update({ business_name: name })
        .eq('id', id)
        .select('id, business_name')
        .single();
      setSavingName(false);
      if (updErr) {
        setNameError('Failed to update name');
        return;
      }
      const nextName = data?.business_name || name;
      setClient((prev) => ({ ...(prev || {}), id: Number(id), business_name: nextName }));
      setEditingName(false);
      updateClientsListCache(id, nextName);
      invalidateClientSnapshot(id);
    } catch {
      setSavingName(false);
      setNameError('Unexpected error updating name');
    }
  };

  const handleAddRegion = async () => {
    const name = (newRegionName || '').trim();
    if (!name) {
      setRegionModalError('Region name is required');
      return;
    }
    setAddingRegion(true);
    const { data, error: insertErr } = await supabase
      .from('regions')
      .insert({ name, client_id: Number(id) })
      .select('id, name')
      .single();
    setAddingRegion(false);
    if (insertErr) {
      setRegionModalError('Failed to create region');
      return;
    }
    setRegions((prev) => [...prev, data].sort((a, b) => String(a.name).localeCompare(String(b.name))));
    setShowRegionModal(false);
    setNewRegionName('');
    invalidateClientSnapshot(id);
  };

  const handleDeleteClient = async () => {
    setDeletingClient(true);
    setDeleteClientError('');
    try {
      await deleteClientById(id);
      setDeletingClient(false);
      setShowDeleteClientModal(false);
      navigate('/admin/clients');
    } catch {
      setDeleteClientError('Failed to delete client');
      setDeletingClient(false);
    }
  };

  return (
    <div className="dashboard-page">
      <RenderMonitor componentName="AdminClientPage" />

      <div className="modern-flex modern-justify-between modern-items-center modern-mb-6">
        <button
          type="button"
          onClick={() => navigate('/admin/clients')}
          className="modern-button modern-button-secondary"
        >
          ← All Clients
        </button>
        <button
          type="button"
          className="modern-button modern-button-danger"
          onClick={() => { setDeleteClientError(''); setShowDeleteClientModal(true); }}
        >
          Delete Client
        </button>
      </div>

      <div className="modern-card modern-mb-6">
        <div className="modern-flex modern-justify-between modern-items-center modern-flex-wrap modern-gap-3">
          <div>
            {editingName ? (
              <div className="modern-flex modern-items-center modern-gap-2 modern-flex-wrap">
                <input
                  type="text"
                  value={newClientName}
                  onChange={(e) => { setNewClientName(e.target.value); setNameError(''); }}
                  className="modern-input"
                  placeholder="Client Name"
                  disabled={savingName}
                  style={{ minWidth: 240 }}
                />
                <button
                  type="button"
                  className="modern-button modern-button-success"
                  disabled={savingName}
                  onClick={handleSaveName}
                >
                  {savingName ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="modern-button modern-button-secondary"
                  disabled={savingName}
                  onClick={() => { setEditingName(false); setNameError(''); }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <h1 className="modern-title" style={{ marginBottom: 4 }}>
                {client?.business_name || 'Client'}
              </h1>
            )}
            {nameError && <div className="modern-error" style={{ marginTop: 8 }}>{nameError}</div>}
          </div>
          <div className="modern-flex modern-items-center modern-gap-2 modern-flex-wrap">
            {!editingName && (
              <button
                type="button"
                className="modern-button modern-button-secondary"
                onClick={() => {
                  setEditingName(true);
                  setNewClientName(client?.business_name || '');
                  setNameError('');
                }}
              >
                Change Client Name
              </button>
            )}
            <button
              type="button"
              className="modern-button modern-button-secondary"
              onClick={() => navigate(`/client/${id}/access`, { state: { scope: 'client' } })}
            >
              Add Client-Level Manager
            </button>
          </div>
        </div>
      </div>

      <div className="modern-flex modern-justify-between modern-items-center modern-mb-4">
        <h2 className="modern-heading" style={{ marginBottom: 0 }}>Regions</h2>
        <button
          type="button"
          className="modern-button modern-button-success"
          onClick={() => { setNewRegionName(''); setRegionModalError(''); setShowRegionModal(true); }}
        >
          + Add Region
        </button>
      </div>

      {loading ? (
        <div className="modern-loading">Loading regions…</div>
      ) : error ? (
        <div className="modern-error">{error}</div>
      ) : (
        <div className="modern-list-panel">
          {regions.length === 0 ? (
            <div className="modern-list-empty">No regions yet. Add a region to organize teams.</div>
          ) : (
            regions.map((region) => {
              const count = teamCounts[String(region.id)] || 0;
              return (
                <div
                  key={region.id}
                  className="modern-list-row modern-list-row-clickable"
                  onClick={() => navigate(`/client/${id}/region/${region.id}`, {
                    state: {
                      clientName: client?.business_name || '',
                      regionName: region.name || '',
                    },
                  })}
                >
                  <span className="modern-list-row-label">{region.name || 'Untitled Region'}</span>
                  <span style={{ color: '#64748b', fontSize: '0.9rem', flexShrink: 0 }}>
                    {count} team{count !== 1 ? 's' : ''}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}

      {showRegionModal && (
        <div className="modern-modal-overlay">
          <div className="modern-modal">
            <button
              type="button"
              onClick={() => setShowRegionModal(false)}
              className="modern-modal-close"
            >
              &times;
            </button>
            <h2 className="modern-heading">Add Region</h2>
            <input
              type="text"
              value={newRegionName}
              onChange={(e) => { setNewRegionName(e.target.value); setRegionModalError(''); }}
              placeholder="Region Name"
              className="modern-input modern-mb-4"
              disabled={addingRegion}
            />
            {regionModalError && <div className="modern-error">{regionModalError}</div>}
            <button
              type="button"
              onClick={handleAddRegion}
              disabled={addingRegion}
              className="modern-button"
              style={{ width: '100%' }}
            >
              {addingRegion ? 'Creating…' : 'Create Region'}
            </button>
          </div>
        </div>
      )}

      {showDeleteClientModal && (
        <div className="modern-modal-overlay">
          <div className="modern-modal" style={{ borderTop: '6px solid #ef4444' }}>
            <button
              type="button"
              onClick={() => setShowDeleteClientModal(false)}
              className="modern-modal-close"
            >
              &times;
            </button>
            <h2 className="modern-heading" style={{ color: '#b91c1c' }}>Delete Client</h2>
            <p style={{ color: '#64748b', marginBottom: 16 }}>
              This will permanently delete this client and all associated teams, team memberships, and bootcamps.
              This action cannot be undone.
            </p>
            {deleteClientError && <div className="modern-error" style={{ marginBottom: 12 }}>{deleteClientError}</div>}
            <div className="modern-flex modern-gap-4">
              <button
                type="button"
                className="modern-button modern-button-danger"
                onClick={handleDeleteClient}
                disabled={deletingClient}
              >
                {deletingClient ? 'Deleting…' : 'Delete Client'}
              </button>
              <button
                type="button"
                className="modern-button modern-button-secondary"
                onClick={() => setShowDeleteClientModal(false)}
                disabled={deletingClient}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminClientPage;
